import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import BetterAnimFrame from "../Common/Utils/BetterAnimFrame";
import { useAppSelector } from "../Common/_hooks/useAppSelector";
import ProductTextureContext from "./_contexts/ProductTextureContext";
import useCustomProductTexture from "./_hooks/useCustomProductTexture";
import { CanvasTexture, LinearFilter, LinearMipmapLinearFilter } from "three";
import type { LabData } from "../AppData/LabData";
import type { ModelViewerMode } from "./ViewerMode";

const TEXTURE_UPDATE_FPS = 30;
const LIVE_TEXTURE_UPDATE_FPS = 20;
const LIVE_TEXTURE_MAX_RESOLUTION = 512;
const LIVE_TEXTURE_SETTLE_DELAY = 300;

function configureTextureQuality(
  texture: CanvasTexture,
  liveQuality: boolean,
  maxAnisotropy: number,
) {
  texture.generateMipmaps = !liveQuality;
  texture.minFilter = liveQuality
    ? LinearFilter
    : LinearMipmapLinearFilter;
  texture.anisotropy = liveQuality ? 1 : maxAnisotropy;
}

interface Props {
  renderTexture: () => HTMLCanvasElement | OffscreenCanvas | HTMLImageElement | undefined
  productId: number,
  labData: LabData,
  options: Immutable.Map<string, string>
  variant: string,
  setProductTexture: (texture:CanvasTexture) => void
  setFxTexture: (texture:CanvasTexture) => void
  onError?: (error: Error) => void
  textureRevision?: number
  viewerMode: ModelViewerMode
  liveTextureUpdates?: boolean
  onTextureUpdated?: (revision?: number) => void
}

/**
 * Composites the Design Lab canvas and product-specific pre/post processing
 * into CanvasTextures. Every successful composite notifies the demand-rendered
 * scene. Lab edits use a smaller texture while moving, then automatically
 * restore the full-quality texture after activity settles. Renderbot revisions
 * are forwarded only after their pixels are written.
 */
export default function ModelViewerTextureUpdater(props: Props) {
  const activeSubproduct = useAppSelector(state => state.get('UIData').get('designLab').get('activeSubproduct'))
  const autoDesign = useAppSelector(state => activeSubproduct ? state.get('UIData').get('designLab').get('autoDesignSubproducts').get(activeSubproduct) : undefined)
  const layers = useAppSelector(state => state.get('UIData').get('designLab').get('layers'))
  const mirrorModes = useAppSelector(state => state.get('UIData').get('designLab').get('mirrorModes'))
  const textureUpdate = useAppSelector(state => state.get('UIData').get('designLab').get('textureUpdate'))

  const [productTexture, fxTexture] = useContext(ProductTextureContext)
  const customProductTexture = useCustomProductTexture(props.productId, props.labData)
  const productTextureCanvas = useRef<OffscreenCanvas>()
  const fxTextureCanvas = useRef<OffscreenCanvas|null>(null)

  const lastFrameTime = useRef(0);
  const updateTimeout = useRef<NodeJS.Timeout | null>(null);
  const settledTextureTimeout = useRef<NodeJS.Timeout | null>(null);
  const textureUpdateMinInterval = 1000 / (
    props.liveTextureUpdates
      ? LIVE_TEXTURE_UPDATE_FPS
      : TEXTURE_UPDATE_FPS
  )

  // Settled previews and renderbot output use the complete configured atlas,
  // including FX textures. Only live editing uses a smaller texture.
  const textureResolutions = useMemo(() => {
    if (props.viewerMode === 'render' || !(props.variant in props.labData.variants)) {
      return {
        live: 1,
        settled: 1,
      };
    }

    const variantData = props.labData.variants[props.variant];
    const sourceWidth = variantData.dimensions.texture.width;

    return {
      live: props.liveTextureUpdates
        ? Math.min(sourceWidth, LIVE_TEXTURE_MAX_RESOLUTION) / sourceWidth
        : 1,
      settled: 1,
    };
  }, [props.labData, props.liveTextureUpdates, props.variant, props.viewerMode])

  const maxAnisotropy = useMemo(() => {
    // Query once using a throwaway context. The result is hardware-dependent
    // and improves oblique fabric detail without increasing atlas dimensions.
    const canvas = new OffscreenCanvas(0, 0)
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl')
    if(!gl) return 1;

    interface AnisotropyExtension {
      MAX_TEXTURE_MAX_ANISOTROPY_EXT: number
    }
    const getExtension = (name:string) => (
      gl.getExtension(name) as AnisotropyExtension|null
    )
    const ext = getExtension("EXT_texture_filter_anisotropic")
      ?? getExtension("MOZ_EXT_texture_filter_anisotropic")
      ?? getExtension("WEBKIT_EXT_texture_filter_anisotropic");

    if (ext) {
      const anisotropy = gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as unknown;
      return typeof anisotropy === 'number' && Number.isFinite(anisotropy)
        ? anisotropy
        : 1;
    } else {
      return 1;
    }
  }, [])

  const renderTextureAtResolution = useCallback(async (
    textureResolution: number,
    liveQuality: boolean,
  ):Promise<boolean> => {
    const currentProductTextureCanvas = productTextureCanvas.current
    const currentFxTextureCanvas = fxTextureCanvas.current
    if (!currentProductTextureCanvas) return false;
    if (productTexture.image !== currentProductTextureCanvas) return false;
    if (
      props.labData.useFxCanvas &&
      (currentFxTextureCanvas === null || fxTexture?.image !== currentFxTextureCanvas)
    ) {
      return false;
    }

    const texture = props.renderTexture();
    if (!texture) return false;

    const variantData = props.labData.variants[props.variant];
    const width = Math.round(variantData.dimensions.texture.width * textureResolution);
    const height = Math.round(variantData.dimensions.texture.height * textureResolution);

    if (
      currentProductTextureCanvas.width !== width
      || currentProductTextureCanvas.height !== height
    ) {
      currentProductTextureCanvas.width = width;
      currentProductTextureCanvas.height = height;
    }
    if (
      currentFxTextureCanvas
      && (
        currentFxTextureCanvas.width !== width
        || currentFxTextureCanvas.height !== height
      )
    ) {
      currentFxTextureCanvas.width = width;
      currentFxTextureCanvas.height = height;
    }

    const context = currentProductTextureCanvas.getContext("2d")
    if (context === null) return false;

    context.clearRect(0, 0, currentProductTextureCanvas.width, currentProductTextureCanvas.height);

    if (!props.labData.transparent_texture) {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, currentProductTextureCanvas.width, currentProductTextureCanvas.height);
    }

    context.globalCompositeOperation = "source-over"

    const optionObject = props.options.toJS()
    const scenes = Object.keys(props.labData.variants[props.variant].scenes)

    // Product hooks can prepare masks/underlays before the Design Lab canvas,
    // then add backing colors or FX atlases after the design has been drawn.
    for (const scene of scenes) {
      await customProductTexture.pre(currentProductTextureCanvas, currentFxTextureCanvas, scene, textureResolution, optionObject, props.variant)
    }

    context.drawImage(texture, 0, 0, currentProductTextureCanvas.width, currentProductTextureCanvas.height)

    for (const scene of scenes) {
      context.globalCompositeOperation = "destination-over"
      await customProductTexture.post(currentProductTextureCanvas, currentFxTextureCanvas, scene, textureResolution, optionObject, props.variant)
      context.globalCompositeOperation = "source-over"
    }

    configureTextureQuality(productTexture, liveQuality, maxAnisotropy)
    if (fxTexture) configureTextureQuality(fxTexture, liveQuality, maxAnisotropy)
    productTexture.needsUpdate = true
    if (fxTexture) fxTexture.needsUpdate = true
    props.onTextureUpdated?.(props.textureRevision)
    return true;
  }, [
    productTexture,
    fxTexture,
    customProductTexture,
    props.renderTexture,
    props.labData,
    props.options,
    props.variant,
    props.textureRevision,
    props.onTextureUpdated,
    maxAnisotropy,
  ])

  const updateTexture = useCallback(async (timestamp:number) => {
    // A new edit supersedes both the throttled update and any pending
    // high-quality settle pass.
    if(updateTimeout.current) {
      clearTimeout(updateTimeout.current);
      updateTimeout.current = null;
    }
    if(settledTextureTimeout.current) {
      clearTimeout(settledTextureTimeout.current);
      settledTextureTimeout.current = null;
    }

    const deltaFrameTime = timestamp - lastFrameTime.current;

    // Drop intermediate frames while editing, but keep a trailing update.
    if(deltaFrameTime < textureUpdateMinInterval) {
      updateTimeout.current = setTimeout(() => {
        updateTimeout.current = null;
        BetterAnimFrame(60)(timestamp => {
          void updateTexture(timestamp)
        });
      }, textureUpdateMinInterval - deltaFrameTime);
      return;
    }

    lastFrameTime.current = timestamp;

    let textureRendered = false
    try {
      textureRendered = await renderTextureAtResolution(
        textureResolutions.live,
        props.liveTextureUpdates === true,
      )
    } catch(error) {
      const textureError = error instanceof Error
        ? error
        : new Error(String(error))
      console.error(textureError)
      props.onError?.(textureError)
      return
    }

    if(!textureRendered) {
      return;
    }

    if(props.liveTextureUpdates) {
      settledTextureTimeout.current = setTimeout(() => {
        settledTextureTimeout.current = null;
        BetterAnimFrame(60)(() => {
          void renderTextureAtResolution(
            textureResolutions.settled,
            false,
          ).catch(error => {
            const textureError = error instanceof Error
              ? error
              : new Error(String(error))
            console.error(textureError)
            props.onError?.(textureError)
          })
        });
      }, LIVE_TEXTURE_SETTLE_DELAY);
    }
  }, [
    props.onError,
    props.liveTextureUpdates,
    renderTextureAtResolution,
    textureResolutions,
    textureUpdateMinInterval,
  ])

  useEffect(() => {
    // Recreate backing canvases and Three textures when the product atlas or
    // quality mode changes. Later edits resize these canvases in place.
    if (!(props.variant in props.labData.variants)) return;

    const variantData = props.labData.variants[props.variant];

    const width = variantData.dimensions.texture.width * textureResolutions.live;
    const height = variantData.dimensions.texture.height * textureResolutions.live;

    productTextureCanvas.current = new OffscreenCanvas(width, height)

    const context = productTextureCanvas.current.getContext('2d');
    if (context === null) return;

    if (props.labData.transparent_texture) {
      context.clearRect(0, 0, productTextureCanvas.current.width, productTextureCanvas.current.height);
    } else {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, productTextureCanvas.current.width, productTextureCanvas.current.height);
    }

    const newProductTexture = new CanvasTexture(productTextureCanvas.current)
    newProductTexture.colorSpace = "srgb"
    configureTextureQuality(newProductTexture, props.liveTextureUpdates === true, maxAnisotropy)

    if (props.labData.useFxCanvas) {
      fxTextureCanvas.current = new OffscreenCanvas(width, height)

      const fxTexture = new CanvasTexture(fxTextureCanvas.current)
      fxTexture.colorSpace = "srgb"
      configureTextureQuality(fxTexture, props.liveTextureUpdates === true, maxAnisotropy)
      props.setFxTexture(fxTexture);
    }

    props.setProductTexture(newProductTexture)
  }, [props.labData.slug, props.variant, props.setProductTexture, props.setFxTexture, props.liveTextureUpdates, maxAnisotropy, textureResolutions.live])

  useEffect(() => () => {
    if(updateTimeout.current) clearTimeout(updateTimeout.current);
    if(settledTextureTimeout.current) clearTimeout(settledTextureTimeout.current);
  }, [updateTexture])

  // Redux's textureUpdate counter is the primary signal, while layer, mirror,
  // subproduct, and variant dependencies cover legacy mutation paths.
  useEffect(() => {
    BetterAnimFrame(60)(timestamp => {
      void updateTexture(timestamp)
    })
  }, [textureUpdate, updateTexture, layers, mirrorModes, props.variant, activeSubproduct, autoDesign])

  return null;
}
